import { useState, useCallback } from "react";

const readExcelFile = async (file) => {
    const XLSX = await import("xlsx");
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
    return rows
        .map((row) => row.map((cell) => String(cell ?? "").trim()).join(","))
        .filter((line) => line.trim());
};

const getFileLines = async (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "xlsx" || ext === "xls") {
        return await readExcelFile(file);
    }

    const text = await file.text();
    return text
        .trim()
        .split(/\r?\n/)
        .filter((l) => l.trim());
};

const groupRoomsForImport = (data) => {
    const grouped = {};
    data.forEach((row) => {
        if (!grouped[row.room]) {
            grouped[row.room] = [];
        }
        grouped[row.room].push(...row.students);
    });
    return Object.entries(grouped).map(([room, students]) => ({
        room,
        students,
    }));
};

const PARSERS = {
    rooms: (lines) => {
        if (lines.length === 0) return [];

        const firstValues = lines[0]
            .split(",")
            .map((v) => v.trim().toLowerCase());

        const hasHeader =
            firstValues[0] === "studentname" ||
            firstValues[0] === "roomname" ||
            firstValues[0] === "sala" ||
            firstValues.some(
                (v) =>
                    v.startsWith("estudiante") ||
                    v.startsWith("student") ||
                    v === "estudiante" ||
                    v === "student" ||
                    v === "alumno" ||
                    v === "alumna",
            );

        if (hasHeader && lines.length < 2) return [];

        const dataLines = hasHeader ? lines.slice(1) : lines;
        const headers = hasHeader ? firstValues : null;

        return dataLines.map((line) => {
            const values = line.split(",").map((v) => v.trim());
            const row = {};
            const students = [];

            if (hasHeader) {
                headers.forEach((h, i) => {
                    const val = values[i] || "";
                    if (h === "roomname" || h === "sala" || h === "room") {
                        row.room = val;
                    } else if (
                        h === "studentname" ||
                        h.startsWith("student") ||
                        h === "students" ||
                        h.startsWith("estudiante") ||
                        h === "estudiantes" ||
                        h === "alumno" ||
                        h === "alumnos" ||
                        h === "alumna" ||
                        h === "alumnas"
                    ) {
                        if (val) students.push(val);
                    }
                });
            } else {
                row.room = values[1] || "";
                if (values[0]) students.push(values[0]);
                values.slice(2).forEach((val) => {
                    if (val) students.push(val);
                });
            }

            row.students = students;
            return row;
        });
    },
    students: (lines) => {
        if (lines.length === 0) return [];

        const first = lines[0].trim().toLowerCase();
        const hasHeader = [
            "name",
            "nombre",
            "student",
            "alumno",
            "students",
            "alumnos",
        ].includes(first);
        const dataLines = hasHeader ? lines.slice(1) : lines;
        return dataLines.map((l) => l.trim()).filter((l) => l);
    },
    teachers: (lines) => {
        if (lines.length < 2) return [];

        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
        return lines.slice(1).map((line) => {
            const values = line.split(",").map((v) => v.trim());
            const row = {};
            headers.forEach((h, i) => {
                row[h] = values[i] || "";
            });
            return row;
        });
    },
};

const ENDPOINTS = {
    rooms: "/api/import/rooms",
    students: (roomId) => `/api/import/students/${roomId}`,
    teachers: "/api/import/teachers",
};

export const useCsvImport = (db, { showAlert, showConfirm }) => {
    const [state, setState] = useState({
        rooms: { file: null, preview: [], importing: false, results: null },
        students: { file: null, preview: [], importing: false, results: null },
        teachers: { file: null, preview: [], importing: false, results: null },
    });

    const setTypeState = useCallback((type, updates) => {
        setState((prev) => ({
            ...prev,
            [type]: { ...prev[type], ...updates },
        }));
    }, []);

    const handleFileChange = useCallback(
        (type) => async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const ext = file.name.split(".").pop()?.toLowerCase();
            const allowedExts = ["csv", "txt", "xlsx", "xls"];
            if (!allowedExts.includes(ext)) {
                showAlert(
                    "Error",
                    `Formato no soportado: .${ext}. Usa CSV o Excel (.xlsx, .xls)`,
                );
                return;
            }

            setTypeState(type, { file, results: null });

            try {
                const lines = await getFileLines(file);
                const parsed = PARSERS[type](lines);

                const preview =
                    type === "rooms"
                        ? groupRoomsForImport(parsed).slice(0, 5)
                        : parsed.slice(0, type === "students" ? 10 : 5);
                setTypeState(type, { preview });
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo leer el archivo: ${error.message}`,
                );
                setTypeState(type, { file: null, preview: [] });
            }
        },
        [setTypeState, showAlert],
    );

    const findInternalDuplicates = useCallback((type, data) => {
        const duplicates = [];
        const seen = new Set();

        if (type === "rooms") {
            const grouped = {};
            data.forEach((row, index) => {
                if (!grouped[row.room]) grouped[row.room] = new Set();
                row.students.forEach((student) => {
                    if (grouped[row.room].has(student))
                        duplicates.push({
                            index,
                            value: student,
                            field: "alumno",
                        });
                    grouped[row.room].add(student);
                });
            });
        } else if (type === "students") {
            data.forEach((name, index) => {
                if (seen.has(name))
                    duplicates.push({ index, value: name, field: "alumno" });
                seen.add(name);
            });
        } else if (type === "teachers") {
            data.forEach((row, index) => {
                const email = row.email || row.correo || "";
                if (email && seen.has(email)) {
                    duplicates.push({ index, value: email, field: "email" });
                }
                if (email) seen.add(email);
            });
        }

        return duplicates;
    }, []);

    const findExistingDuplicates = useCallback(
        async (type, data, extraParams) => {
            const duplicates = [];

            try {
                if (type === "rooms") {
                    const roomNames = new Set(data.map((r) => r.room));
                    const existingRooms = await db.getTeacherRooms();
                    const existingNames = new Set(
                        existingRooms.map((r) => r.name),
                    );
                    roomNames.forEach((roomName) => {
                        if (existingNames.has(roomName)) {
                            const firstIndex = data.findIndex(
                                (r) => r.room === roomName,
                            );
                            duplicates.push({
                                index: firstIndex,
                                value: roomName,
                            });
                        }
                    });
                } else if (type === "students") {
                    if (extraParams) {
                        const room = await db.getRoomById(extraParams);
                        if (room && room.allowedStudents) {
                            const existingStudents = new Set(
                                room.allowedStudents,
                            );
                            data.forEach((name, index) => {
                                if (existingStudents.has(name))
                                    duplicates.push({ index, value: name });
                            });
                        }
                    }
                } else if (type === "teachers") {
                    const existingTeachers = await db.getTeachers();
                    const existingEmails = new Set(
                        existingTeachers.map((t) => t.email),
                    );
                    data.forEach((row, index) => {
                        const email = row.email || row.correo || "";
                        if (email && existingEmails.has(email))
                            duplicates.push({ index, value: email });
                    });
                }
            } catch (error) {
                showAlert(
                    "Error",
                    `Error buscando entradas duplicadas: ${error.message}`,
                );
            }

            return duplicates;
        },
        [db, showAlert],
    );

    const importCsv = useCallback(
        async (type, extraParams = null) => {
            const { file } = state[type];
            if (!file) return null;

            try {
                const lines = await getFileLines(file);
                const data = PARSERS[type](lines);
                if (data.length === 0) {
                    showAlert("Error", "Archivo vacío o mal formateado");
                    return;
                }

                const internalDuplicates = findInternalDuplicates(type, data);
                const existingDuplicates = await findExistingDuplicates(
                    type,
                    data,
                    extraParams,
                );
                const allDuplicates = [
                    ...internalDuplicates,
                    ...existingDuplicates,
                ];
                const length = allDuplicates.length;
                const s = length > 1 ? "s" : "";
                const d = length > 1 ? "aron" : "ó";
                const a = type === "rooms" ? "a" : "o";

                if (length > 0) {
                    const t =
                        type === "rooms"
                            ? `sala${s}`
                            : type === "students"
                              ? `alumno${s}`
                              : `profesore${s}`;
                    const message = `Se encontr${d} ${allDuplicates.length} ${t} duplicad${a}${s}. ¿Deseas continuar e ignorar los duplicados? Esto es irreversible de forma automática.`;
                    return new Promise((resolve) => {
                        showConfirm({
                            title: "Duplicados detectados",
                            message: message,
                            danger: false,
                            confirm: "Continuar",
                            cancel: "Cancelar",
                            onConfirm: async () => {
                                const result = await executeImport(
                                    type,
                                    data,
                                    extraParams,
                                );
                                resolve(result);
                            },
                            onClose: () => {
                                resolve(null);
                            },
                        });
                    });
                }

                return await executeImport(type, data, extraParams);
            } catch (error) {
                showAlert(
                    "Error",
                    `Problema al importar el archivo: ${error.message}`,
                );
            }
        },
        [
            state,
            db,
            showAlert,
            showConfirm,
            findInternalDuplicates,
            findExistingDuplicates,
        ],
    );

    const executeImport = useCallback(
        async (type, data, extraParams = null) => {
            setTypeState(type, { importing: true, results: null });
            try {
                const endpoint =
                    typeof ENDPOINTS[type] === "function"
                        ? ENDPOINTS[type](extraParams)
                        : ENDPOINTS[type];
                const body =
                    type === "students"
                        ? { students: data }
                        : type === "rooms"
                          ? { rows: groupRoomsForImport(data) }
                          : { rows: data };
                const result = await db.apiRequest(endpoint, {
                    method: "POST",
                    body: JSON.stringify(body),
                });
                const normalized =
                    type === "students" ? result : result.results || result;
                setTypeState(type, { results: normalized });
                return normalized;
            } catch (error) {
                showAlert(
                    "Error",
                    `Problema al importar el archivo: ${error.message}`,
                );
            } finally {
                setTypeState(type, { importing: false });
            }
        },
        [db, showAlert, setTypeState],
    );

    const reset = useCallback(
        (type) => {
            setTypeState(type, { file: null, preview: [], results: null });
        },
        [setTypeState],
    );

    return {
        rooms: state.rooms,
        students: state.students,
        teachers: state.teachers,
        handleFileChange: (type) => handleFileChange(type),
        importCsv: (type, extraParams) => importCsv(type, extraParams),
        reset: (type) => reset(type),
    };
};
